import React, { useEffect, useMemo, useState } from "react";
import {
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Box,
  Text,
  Flex,
  Badge,
  Button,
  Skeleton,
} from "@chakra-ui/react";
import { FaDirections } from "react-icons/fa";
import { GoogleMap } from "../../Services/GoogleMapsComponent";
import googleMapsService from "../../Services/GoogleMapsService";
import { buildPinElement } from "../Components/mapPins";
import { bottomDrawerSize } from "./drawerSizing";
import { useDrawerSettled } from "./useDrawerSettled";
import { PROPERTY_CATEGORY } from "../Logic/guideCategories";
import { PROPERTY_CATALOG_NAME } from "../Logic/propertyGuideItems";

// The room stores its address as JSON from Places autocomplete; older rooms
// store plain text.
const parseAddress = (raw) => {
  if (!raw || typeof raw !== "string") return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object"
      ? parsed
      : { formatted_address: raw };
  } catch (error) {
    return { formatted_address: raw };
  }
};

// Same shell as the rest of the hub's drawers (GuideDrawer,
// PropertySectionDrawer): bottom sheet, same header, same sizing. The body is
// the property's map, its address, and one button out to the Maps app.
export const DirectionsDrawer = ({ isOpen, onClose, room }) => {
  const settled = useDrawerSettled(isOpen);
  const [propertyLocation, setPropertyLocation] = useState(null);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [geocodeFailed, setGeocodeFailed] = useState(false);

  const addressData = useMemo(() => parseAddress(room?.address), [room?.address]);
  const addressText = addressData?.formatted_address || "";

  // Geocoding is billed per request, and this drawer is mounted for the whole
  // life of the hub page — so look the property up only once someone opens it,
  // and only once per address.
  useEffect(() => {
    if (!isOpen || !addressData || propertyLocation) return undefined;

    let cancelled = false;

    const geocodeProperty = async () => {
      try {
        setIsGeocoding(true);
        setGeocodeFailed(false);

        // A stored Place ID is the most accurate input; otherwise try to find
        // one from the text, and fall back to plain address geocoding.
        let geocodeInput;
        if (addressData.place_id) {
          geocodeInput = { placeId: addressData.place_id };
        } else if (addressData.formatted_address) {
          try {
            const found = await googleMapsService.findPlaceId(
              addressData.formatted_address
            );
            geocodeInput = found?.place_id
              ? { placeId: found.place_id }
              : { address: addressData.formatted_address };
          } catch (placeIdError) {
            geocodeInput = { address: addressData.formatted_address };
          }
        }

        if (!geocodeInput) {
          if (!cancelled) setGeocodeFailed(true);
          return;
        }

        const location = await googleMapsService.geocodeAddress(geocodeInput);
        if (cancelled) return;

        setPropertyLocation({
          lat: location.lat,
          lng: location.lng,
          formattedAddress: location.formattedAddress || addressText,
          placeId: addressData.place_id || location.placeId || undefined,
        });
      } catch (error) {
        console.error("Error geocoding property address:", error);
        if (!cancelled) setGeocodeFailed(true);
      } finally {
        if (!cancelled) setIsGeocoding(false);
      }
    };

    geocodeProperty();

    return () => {
      cancelled = true;
    };
  }, [isOpen, addressData, addressText, propertyLocation]);

  // A new room means a new location.
  useEffect(() => {
    setPropertyLocation(null);
    setGeocodeFailed(false);
  }, [room?.address]);

  // Same home pin as the hub's Map. Built once so the map isn't handed a new
  // element on every render.
  const markers = useMemo(() => {
    if (!propertyLocation) return [];
    return [
      {
        position: { lat: propertyLocation.lat, lng: propertyLocation.lng },
        options: {
          content: buildPinElement(PROPERTY_CATEGORY, true),
          title: propertyLocation.formattedAddress,
        },
      },
    ];
  }, [propertyLocation]);

  const openInMaps = () => {
    // Works even when geocoding failed: the address text is enough for Maps.
    const location = propertyLocation || {};
    const formattedAddress = location.formattedAddress || addressText;
    const { lat, lng, placeId } = location;

    let url;
    if (placeId && formattedAddress) {
      url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        formattedAddress
      )}&query_place_id=${placeId}`;
    } else if (placeId) {
      url = `https://www.google.com/maps/search/?api=1&query_place_id=${placeId}`;
    } else if (formattedAddress) {
      url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        formattedAddress
      )}`;
    } else if (lat != null && lng != null) {
      url = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
    } else {
      return;
    }

    window.open(url, "_blank");
  };

  const mapHeight = "260px";

  return (
    <Drawer isOpen={isOpen} placement="bottom" onClose={onClose} size="full">
      <DrawerOverlay />
      <DrawerContent
        bg="#1A202C"
        color="white"
        h="auto"
        {...bottomDrawerSize({ mobile: 80, desktop: 88 })}
        borderTopRadius="20px"
        display="flex"
        flexDirection="column"
        maxW={{ base: "100%", md: "640px" }}
        mx="auto"
        mb={{ md: 8 }}
        borderRadius={{ md: "24px" }}
      >
        <DrawerHeader
          px={{ base: 4, md: 6 }}
          py={4}
          borderBottom="1px solid"
          borderColor="rgba(255, 255, 255, 0.1)"
          flexShrink={0}
        >
          <Flex alignItems="center" justifyContent="space-between">
            <Flex direction="column">
              <Text fontSize={{ base: "16px", md: "18px" }} fontWeight="700" lineHeight="1.2">
                Directions
              </Text>
              <Badge
                colorScheme="purple"
                variant="subtle"
                fontSize="10px"
                px={2}
                py={1}
                mt={1}
                alignSelf="flex-start"
              >
                {PROPERTY_CATALOG_NAME}
              </Badge>
            </Flex>
            <DrawerCloseButton pos="relative" top={0} right={0} />
          </Flex>
        </DrawerHeader>

        <DrawerBody
          px={{ base: 4, md: 6 }}
          pt={{ base: 4, md: 5 }}
          pb={6}
          overflowY="auto"
          flex={1}
          minH={0}
        >
          <Box borderRadius="12px" overflow="hidden" bg="#2D3748">
            {/* The map mounts only after the slide-in is done — initializing
                Google Maps mid-animation makes the sheet stutter. The
                skeleton is the same height, so nothing moves when it lands. */}
            {!settled || isGeocoding || (!propertyLocation && !geocodeFailed) ? (
              <Skeleton h={mapHeight} />
            ) : propertyLocation ? (
              <GoogleMap
                center={propertyLocation}
                zoom={16}
                height={mapHeight}
                markers={markers}
                // One-finger drag should move the map; it sits inside a
                // scrolling sheet, but the sheet only scrolls past the map.
                gestureHandling="greedy"
                fullscreenControl={false}
              />
            ) : (
              <Flex h={mapHeight} align="center" justify="center" px={6} textAlign="center">
                <Text fontSize="13px" color="gray.400">
                  We couldn't show this address on the map, but you can still
                  open it in Maps below.
                </Text>
              </Flex>
            )}
          </Box>

          <Text
            mt={4}
            fontSize="11px"
            fontWeight={600}
            letterSpacing="0.06em"
            textTransform="uppercase"
            color="#A5A3FF"
          >
            Property address
          </Text>
          <Text mt={1} fontSize="14px" fontWeight={700} color="white">
            {propertyLocation?.formattedAddress || addressText}
          </Text>

          <Button
            mt={4}
            w="full"
            h="42px"
            leftIcon={<FaDirections />}
            bg="#817EFF"
            color="white"
            borderRadius="10px"
            fontSize="14px"
            fontWeight={700}
            _hover={{ bg: "#615EDC" }}
            _active={{ bg: "#615EDC", transform: "scale(0.99)" }}
            onClick={openInMaps}
            isDisabled={!propertyLocation && !addressText}
          >
            Open in Maps
          </Button>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};
